import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * AdminBookingsWipe — bookings-only deletion with mandatory dry-run.
 *
 * STRICTLY narrower than AdminWipeTestData — this page deletes ONLY the
 * `bookings` collection. Clients, consent forms, consent submissions,
 * medical questionnaires, treatments, batch records, payment records,
 * loyalty cards, credit ledger entries, stock inventory and admin
 * settings are all explicitly preserved.
 *
 * Flow:
 *   1. Admin clicks "Load preview" → `GET /api/admin/bookings-wipe/preview`
 *   2. Table lists every booking that would be deleted (ref, name, email, date, status).
 *   3. Admin types the exact confirmation phrase + password and clicks
 *      "Delete all bookings" → `POST /api/admin/bookings-wipe/execute`.
 *   4. Server deletes and writes an admin_audit row; UI shows the
 *      deleted count and the audit id.
 */

const CONFIRM_PHRASE = "I understand this will permanently delete all bookings.";

export default function AdminBookingsWipe() {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [executing, setExecuting] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [phrase, setPhrase] = React.useState("");
  const [result, setResult] = React.useState(null);

  const loadPreview = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.get("/admin/bookings-wipe/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const executeWipe = async () => {
    if (phrase.trim() !== CONFIRM_PHRASE) {
      toast.error("Confirmation phrase does not match exactly");
      return;
    }
    if (!password) {
      toast.error("Password is required");
      return;
    }
    if (!window.confirm(`Delete ${preview?.total ?? 0} booking(s)? This cannot be undone.`)) return;
    setExecuting(true);
    try {
      const r = await api.post("/admin/bookings-wipe/execute", {
        password,
        confirm_phrase: phrase.trim(),
      });
      setResult(r.data);
      setPreview(null);
      setPhrase("");
      setPassword("");
      toast.success(`Deleted ${r.data.deleted_count} booking(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div data-testid="admin-bookings-wipe">
      <h2 className="font-serif text-4xl font-light">Bookings-Only Wipe</h2>
      <p className="mt-2 text-sm text-stone-600 max-w-2xl">
        Deletes <strong>only</strong> booking records from the connected database. Clients,
        consent forms, medical questionnaires, treatments, batch records, payment records,
        loyalty cards, credit ledger entries, stock inventory and admin settings are all
        explicitly preserved. Load the preview first to review every booking that would be
        deleted before confirming.
      </p>

      {result && (
        <div
          data-testid="wipe-result"
          className="mt-6 border hairline bg-emerald-50 border-emerald-300 p-4"
        >
          <p className="mono-label text-[11px] tracking-[0.22em] text-emerald-900">
            DELETION COMPLETE
          </p>
          <p className="mt-2 text-sm text-stone-900">
            Deleted <strong>{result.deleted_count}</strong> booking record{result.deleted_count === 1 ? "" : "s"}.
          </p>
          <p className="mt-1 text-xs text-stone-600">Audit id: <code>{result.audit_id}</code></p>
          <p className="mt-2 text-xs text-stone-600">
            Preserved collections: {result.preserved_scope.join(", ")}
          </p>
        </div>
      )}

      {!preview && !result && (
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading}
          data-testid="load-preview"
          className="mt-6 bg-stone-900 text-white px-5 py-3 mono-label text-[11px] tracking-[0.22em] disabled:opacity-60"
          style={{ color: "#fff" }}
        >
          {loading ? "LOADING…" : "LOAD PREVIEW"}
        </button>
      )}

      {preview && (
        <div className="mt-6 space-y-5">
          <div className="border hairline p-4 bg-alt">
            <p className="mono-label text-[11px] tracking-[0.22em] text-stone-700">
              {preview.total} BOOKING RECORD{preview.total === 1 ? "" : "S"} WILL BE DELETED
            </p>
            <p className="mt-2 text-xs text-stone-600">
              Preserved alongside: {Object.entries(preview.preserved)
                .map(([k, v]) => `${k} (${v})`).join(", ")}.
              These counts should remain unchanged after the wipe.
            </p>
          </div>

          <div className="border hairline bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="bg-stone-50">
                <tr className="text-left mono-label text-[10px] tracking-[0.18em] text-stone-500">
                  <th className="px-3 py-2">REFERENCE</th>
                  <th className="px-3 py-2">CLIENT NAME</th>
                  <th className="px-3 py-2">EMAIL</th>
                  <th className="px-3 py-2">DATE</th>
                  <th className="px-3 py-2">TIME</th>
                  <th className="px-3 py-2">STATUS</th>
                  <th className="px-3 py-2 text-right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {preview.bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-stone-500">
                    No bookings in the database — nothing to delete.
                  </td></tr>
                )}
                {preview.bookings.map((b) => (
                  <tr key={b.id || b.reference} className="border-t hairline">
                    <td className="px-3 py-2 font-mono">{b.reference || "—"}</td>
                    <td className="px-3 py-2">{b.client_name || "—"}</td>
                    <td className="px-3 py-2 text-stone-600">{b.client_email || "—"}</td>
                    <td className="px-3 py-2">{b.date || "—"}</td>
                    <td className="px-3 py-2">{b.time || "—"}</td>
                    <td className="px-3 py-2">{b.appointment_status || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {typeof b.final_total === "number" ? `£${b.final_total.toFixed(2)}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.total > 0 && (
            <div className="border hairline border-red-300 bg-red-50 p-4 space-y-3">
              <p className="mono-label text-[11px] tracking-[0.22em] text-red-900">
                CONFIRM DELETION
              </p>
              <p className="text-xs text-stone-700">
                Type this phrase exactly: <code className="bg-white px-1">{CONFIRM_PHRASE}</code>
              </p>
              <input
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder="Confirmation phrase"
                data-testid="confirm-phrase"
                className="w-full border hairline p-2 text-sm font-mono"
                autoComplete="off"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your admin password"
                data-testid="confirm-password"
                className="w-full border hairline p-2 text-sm"
                autoComplete="current-password"
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setPreview(null); setPhrase(""); setPassword(""); }}
                  className="border hairline px-4 py-2 mono-label text-[11px] tracking-[0.22em]"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={executeWipe}
                  disabled={executing || phrase.trim() !== CONFIRM_PHRASE || !password}
                  data-testid="execute-wipe"
                  className="bg-red-800 text-white px-5 py-2 mono-label text-[11px] tracking-[0.22em] disabled:opacity-50"
                  style={{ color: "#fff" }}
                >
                  {executing ? "DELETING…" : `DELETE ${preview.total} BOOKING${preview.total === 1 ? "" : "S"}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
